// deleteAccount.js — "Delete my account" in Settings (GDPR Article 17,
// CCPA's right to delete). Two steps, in this order:
//   1. delete_my_account() (supabase/migrations/20261022000000_delete_my_account.sql),
//      which deletes every row that holds them and then the account itself;
//   2. the person's own avatar files (Storage can't be cleared from SQL),
//      removed with the access token, which stays good until it expires.
// This order means a failed step 1 leaves the account exactly as it was.
// The caller then clears this device (deviceData.js) and ends the session.
// Until the migration is live, step 1 fails with a plain message.

import { supabase } from "./supabase";

export class DeleteAccountError extends Error {
  constructor(kind, cause) {
    super(kind);
    this.kind = kind; // "unavailable" | "failed"
    this.cause = cause;
  }
}

// PostgREST answers PGRST202 when the function isn't there yet.
const isMissingFunction = (error) =>
  error?.code === "PGRST202" || /could not find the function/i.test(error?.message || "");

async function removeAvatars(userId) {
  const bucket = supabase.storage.from("avatars");
  const { data, error } = await bucket.list(userId, { limit: 1000 });
  if (error) throw error;
  const paths = (data || []).filter((f) => f?.name).map((f) => `${userId}/${f.name}`);
  if (!paths.length) return;
  const { error: rmErr } = await bucket.remove(paths);
  if (rmErr) throw rmErr;
}

export async function deleteMyAccount(user) {
  if (!user?.id) throw new DeleteAccountError("failed");
  let result;
  try {
    result = await supabase.rpc("delete_my_account");
  } catch (e) {
    throw new DeleteAccountError("failed", e);
  }
  if (result?.error) {
    throw new DeleteAccountError(isMissingFunction(result.error) ? "unavailable" : "failed", result.error);
  }
  // The account is gone; the photo follows. One retry, then it's logged
  // rather than shown, since there's no account left to try again from.
  for (let attempt = 0; attempt < 2; attempt++) {
    try { await removeAvatars(user.id); return; } catch (e) {
      if (attempt) console.error("Avatar removal failed after the account was deleted:", e);
    }
  }
}
